import type { AgentChatMessage } from "@coeff/api";

type Message = AgentChatMessage;

export interface TurnRevertUiMeta {
  showDashboardActionCard: boolean;
  canRevert: boolean;
  revertTargetMessageId?: string;
  actionLabelOverride?: string;
}

interface ComputeTurnRevertUiParams {
  messages: Message[];
  isThinking: boolean;
  messageUndoEntryIds?: Map<string, string>;
  undoStack?: Array<{ id: string }>;
}

/**
 * Computes turn-level revert UI metadata keyed by the display message ID.
 *
 * A "turn" is the sequence of non-event assistant messages after a user message
 * until the next user message.
 */
export function computeTurnRevertUiByDisplayMessageId({
  messages,
  isThinking,
  messageUndoEntryIds,
  undoStack,
}: ComputeTurnRevertUiParams): Map<string, TurnRevertUiMeta> {
  const byDisplayMessageId = new Map<string, TurnRevertUiMeta>();
  const nonEventMessages = messages.filter(m => m.role !== "event");
  const lastNonEventMessageId = nonEventMessages[nonEventMessages.length - 1]?.id;

  let assistantMessageIdsInTurn: string[] = [];
  let firstUpdateMessageIdInTurn: string | undefined;
  let lastAssistantActionLabelInTurn: string | undefined;

  const finalizeTurn = () => {
    if (assistantMessageIdsInTurn.length === 0) {
      return;
    }

    const displayMessageId = assistantMessageIdsInTurn[assistantMessageIdsInTurn.length - 1];
    const turnIsStillInFlight = isThinking && displayMessageId === lastNonEventMessageId;

    if (firstUpdateMessageIdInTurn && !turnIsStillInFlight) {
      const undoEntryId = messageUndoEntryIds?.get(firstUpdateMessageIdInTurn);
      const canRevert = Boolean(undoEntryId && undoStack?.some(entry => entry.id === undoEntryId));

      byDisplayMessageId.set(displayMessageId, {
        showDashboardActionCard: true,
        canRevert,
        revertTargetMessageId: firstUpdateMessageIdInTurn,
        actionLabelOverride: lastAssistantActionLabelInTurn || "Updated dashboard",
      });
    }
  };

  for (const msg of nonEventMessages) {
    if (msg.role === "user") {
      finalizeTurn();
      assistantMessageIdsInTurn = [];
      firstUpdateMessageIdInTurn = undefined;
      lastAssistantActionLabelInTurn = undefined;
      continue;
    }

    assistantMessageIdsInTurn.push(msg.id);
    lastAssistantActionLabelInTurn = msg.content?.tool_call_summary_label || undefined;

    const hasDashboardUpdateInMessage = Boolean(
      msg.content?.dashboard_updated || msg.content?.web_dashboard_json?.content_config
    );
    if (!firstUpdateMessageIdInTurn && hasDashboardUpdateInMessage) {
      firstUpdateMessageIdInTurn = msg.id;
    }
  }

  finalizeTurn();
  return byDisplayMessageId;
}
